export const siteData = {
  siteName: "Business-Catalysts",
  
  home: {
    headline: "Designing the execution architecture behind complex technology and infrastructure platforms.",
    tagline: "Aligning capital, ecosystems, governance, operating models, and execution systems so large-scale platforms scale with discipline, resilience, and long-term durability.",
    supportingLine: "Business-Catalysts operates at the intersection of enterprise execution, ecosystem coordination, infrastructure strategy, and AI-era operating evolution.",
    
    positioning: {
      title: "What Business-Catalysts Does",
      paragraphs: [
        "Business-Catalysts advises enterprises, infrastructure platforms, ecosystem leaders, and growth-stage organisations on execution architecture, governance systems, commercial operating models, and strategic scale transitions.",
        "The focus is not implementation volume.",
        "It is institutional coherence.",
        "As AI systems, infrastructure platforms, and ecosystem-led growth models scale, operational fragmentation becomes a strategic risk.",
        "Business-Catalysts works with leadership teams to design systems that scale responsibly, commercially, and durably."
      ]
    },
    
    modernization: {
      title: "Enterprise Modernization in the AI Era",
      paragraphs: [
        "Most organisations today are not constrained by access to AI capability.",
        "They are constrained by operational integration, governance readiness, ecosystem coordination, modernization sequencing, and execution discipline.",
        "As AI capabilities move from experimentation into enterprise operating environments, complexity increases across infrastructure, workflows, accountability systems, partnerships, and decision architecture.",
        "Business-Catalysts works with enterprises navigating modernization environments where operating models, infrastructure systems, governance frameworks, ecosystem partnerships, and AI-enabled capabilities must evolve together.",
        "The challenge is rarely experimentation.",
        "It is operational transition at institutional scale."
      ],
      micro: [
        "AI amplifies capability.",
        "Execution determines whether capability compounds sustainably."
      ]
    },
    
    commercialArchitecture: {
      title: "Designing the Commercial Architecture of Complex Platforms",
      intro: [
        "Business-Catalysts operates at the commercial architecture layer of complex infrastructure and technology platforms.",
        "As capital, partners, and markets scale, execution often fragments.",
        "What determines whether large platforms succeed is not technology or funding alone, but the architecture through which capital, demand, ecosystems, and governance align over time.",
        "Business-Catalysts works with leaders to design these systems before large scale execution begins."
      ],
      subheading: "The Commercial Architecture Layer",
      subheadingIntro: "Large platforms scale through four interconnected domains.",
      domains: [
        {
          title: "Capital Architecture",
          description: "Designing how capital enters, sequences, and compounds across infrastructure growth cycles."
        },
        {
          title: "Market Architecture",
          description: "Structuring how demand forms and expands across markets."
        },
        {
          title: "Ecosystem Architecture",
          description: "Aligning builders, operators, technology partners, and capital partners into coherent platforms."
        },
        {
          title: "Governance Architecture",
          description: "Designing decision frameworks and institutional discipline as platforms scale."
        }
      ],
      closing: [
        "Execution partners build and operate the platform.",
        "Business-Catalysts designs how the system works."
      ]
    },
    
    context: {
      title: "The Context",
      paragraphs: [
        "Most enterprises today are not constrained by ideas, capital, or technology.",
        "They are constrained by execution.",
        "As AI and digital capability accelerate, the challenge has shifted — from what is possible to what can be operated responsibly, at scale, and with trust.",
        "Strategy often exists.",
        "Technology often exists.",
        "Outcomes fail when systems, ownership, and governance do not."
      ]
    },
    
    role: {
      title: "Our Role",
      paragraphs: [
        "Business-Catalysts works in the space between intent and outcome.",
        "We help leaders move from fragmented initiatives to coherent operating systems — where strategy, technology, people, and governance are aligned to deliver durable results.",
        "Our focus is not on tools or trends.",
        "It is on designing and operating systems that hold under real-world complexity."
      ]
    },
    
    philosophy: {
      title: "Our Philosophy",
      points: [
        "Systems, not point solutions",
        "Operating models, not pilots",
        "Clear ownership, not consensus theatre",
        "Governance by design, not afterthought",
        "Execution that compounds quietly over time"
      ],
      closing: "We work with restraint, discretion, and a bias for clarity — especially in environments where reputation, trust, and accountability matter."
    },
    
    execution: {
      title: "How We Think About Execution",
      intro: "Our work is anchored in a disciplined, system-level approach we call the Execution OS — a way of translating ambition into reality.",
      connectsLabel: "It connects:",
      connects: [
        "Strategy and intent",
        "Capability and architecture",
        "Operating model and governance",
        "Orchestration and delivery",
        "Outcomes and feedback"
      ],
      closing: [
        "This is not a framework for presentations.",
        "It is a way of running change.",
        "(Details are shared selectively.)"
      ]
    }
  },
  
  approach: {
    title: "Approach",
    subtitle: "The Execution OS",
    
    why: {
      title: "Why an Execution OS",
      paragraphs: [
        "Most organisations do not fail because of poor strategy.",
        "They fail because execution becomes fragmented as complexity increases.",
        "As AI systems move from experimentation into enterprise operations, the gap between strategy and execution widens due to fragmented ownership, weak governance structures, ecosystem misalignment, and operating complexity.",
        "The Execution OS exists to close that gap."
      ]
    },
    
    what: {
      title: "What the Execution OS Is",
      paragraphs: [
        "The Execution OS is a system-level approach for turning ambition into outcomes.",
        "It is not a consulting framework.",
        "It is not a methodology for slides.",
        "It is a way of designing and running execution across strategy, technology, people, and governance — as one integrated system.",
        "The Execution OS is designed for environments where technology, infrastructure, capital, partnerships, and governance must evolve together.",
        "This becomes increasingly important in AI-enabled enterprises where scale amplifies operational complexity rather than reducing it."
      ]
    },
    
    how: {
      title: "How the Execution OS Works",
      steps: [
        {
          number: "1",
          title: "Strategy & Intent",
          description: "Clarifying what truly matters — and what does not."
        },
        {
          number: "2",
          title: "Capability & Architecture",
          description: "Technology, data, partners, talent, and processes are mapped into a coherent system."
        },
        {
          number: "3",
          title: "Operating Model & Governance",
          description: "Decision rights, accountability, controls, and escalation paths are defined early."
        },
        {
          number: "4",
          title: "Orchestration & Delivery",
          description: "Teams, partners, and initiatives are coordinated through a clear execution cadence."
        },
        {
          number: "5",
          title: "Outcomes & Feedback Loops",
          description: "Outcomes are measured and feedback allows adaptation without loss of control."
        }
      ]
    },
    
    different: {
      title: "What Makes This Different",
      points: [
        "System thinking over point optimisation",
        "Operating models over isolated initiatives",
        "Governance embedded in design",
        "Execution discipline over experimentation theatre"
      ]
    },
    
    infrastructureArchitecture: {
      title: "Commercial Architecture for Infrastructure Platforms",
      intro: [
        "In large infrastructure platforms, execution complexity increases rapidly as capital, partners, markets, and governance structures expand.",
        "Technology and capital alone rarely determine success.",
        "What determines success is the commercial architecture governing how these systems scale.",
        "Business-Catalysts operates at this architecture layer.",
        "We work with infrastructure platforms to design the structures through which capital, markets, ecosystems, and governance align over time."
      ],
      subheading: "The Commercial Architecture Layer",
      subheadingIntro: "Our work focuses on four interdependent domains.",
      domains: [
        {
          title: "Capital Architecture",
          description: "Designing how capital enters, sequences, and compounds across infrastructure growth cycles.",
          examples: "Examples include anchor demand logic, capital staging, and project bankability structures."
        },
        {
          title: "Market Architecture",
          description: "Structuring how demand forms and expands across markets.",
          examples: "Examples include hyperscaler engagement models, demand aggregation, and geographic expansion pathways."
        },
        {
          title: "Ecosystem Architecture",
          description: "Aligning builders, operators, technology partners, and capital providers into coherent platforms.",
          examples: "This includes partner alignment frameworks and ecosystem coordination."
        },
        {
          title: "Governance Architecture",
          description: "Designing decision frameworks and institutional discipline as platforms scale.",
          examples: "This includes joint venture governance, expansion decision rights, and long term platform stewardship."
        }
      ],
      closing: [
        "Execution partners build and operate the platform.",
        "Business-Catalysts designs how the system works."
      ]
    },
    
    closing: {
      title: "Closing Thought",
      paragraphs: [
        "Execution is not a phase.",
        "It is an operating condition."
      ]
    }
  },
  
  engagements: {
    title: "Engagements",
    
    intro: {
      title: "How We Engage",
      paragraphs: [
        "Business-Catalysts does not operate as a traditional consultancy.",
        "We engage selectively where execution quality has disproportionate impact."
      ]
    },
    
    archetypes: {
      title: "Engagement Archetypes",
      items: [
        {
          title: "Founder & Board Advisory",
          description: "Supporting founders and boards during scale, transition, or reset."
        },
        {
          title: "Enterprise Execution Partnerships",
          description: "Partnering with enterprises moving from experimentation to operation."
        },
        {
          title: "Public & Regulated Environments",
          description: "Working where trust, accountability, and governance are essential."
        },
        {
          title: "AI & Enterprise Operating Transitions",
          description: "Supporting enterprises and ecosystem leaders as AI capabilities move from experimentation into operational environments requiring governance, execution discipline, commercial alignment, and institutional accountability."
        }
      ]
    },
    
    notList: {
      title: "What Engagements Are Not",
      points: [
        "Staff augmentation",
        "Technology implementation",
        "Vendor selection",
        "Short-term retainers",
        "Slide-driven strategy"
      ]
    },
    
    howWeWork: {
      title: "How We Work",
      points: [
        "Senior-led.",
        "Outcome-oriented.",
        "Discreet and trusted."
      ]
    },
    
    next: {
      title: "Next",
      email: "irfan@business-catalysts.com"
    }
  },
  
  infrastructure: {
    title: "Infrastructure & Data Centre Commercial Architecture",
    
    intro: {
      paragraphs: [
        "Capital-intensive platforms do not fail because of engineering.",
        "They fail because capital sequencing lacks discipline.",
        "Business-Catalysts advises on the commercial architecture behind infrastructure and data centre investments.",
        "Our focus is not engineering execution alone.",
        "It is commercial durability, governance discipline, capital protection, ecosystem coordination, and long-term operational resilience.",
        "As AI infrastructure, compute ecosystems, and data-intensive platforms scale globally, execution quality increasingly depends on how capital, governance, ecosystem alignment, and operational accountability evolve together."
      ]
    },
    
    whyMatters: {
      title: "Why This Matters",
      paragraphs: [
        "In high-capex environments, scale announcements are easy.",
        "Durable underwriting is not.",
        "The difference between expansion and value creation is sequencing."
      ]
    },
    
    whereOperate: {
      title: "Where We Operate",
      points: [
        "Anchor tenant underwriting logic",
        "Hyperscaler engagement structuring",
        "Take-or-pay and revenue durability frameworks",
        "Modular deployment and expansion gating",
        "Capital staging logic across 5–25 MW growth cycles",
        "Governance architecture for sovereign and mission-critical contexts",
        "GTM articulation post-commissioning"
      ]
    },
    
    whatWeDoNot: {
      title: "What We Do Not Do",
      points: [
        "EPC execution or civil supervision",
        "Detailed engineering certification",
        "Hardware resale or brokerage",
        "Statutory approvals or compliance representation"
      ],
      closing: [
        "Engineering enables delivery.",
        "Commercial architecture determines capital resilience."
      ]
    },
    
    typicalContexts: {
      title: "Typical Contexts",
      points: [
        "Sovereign data centre initiatives",
        "Modular 5 MW to 25 MW expansion pathways",
        "SI partnerships requiring commercial framing support",
        "Board-level capital sequencing reviews",
        "Institutional capital underwriting validation"
      ],
      closing: "Engagements are selective and structured."
    },
    
    contact: {
      label: "For discussion:",
      email: "irfan@business-catalysts.com"
    },
    
    energy: {
      title: "Energy Infrastructure Commercial Architecture",
      paragraphs: [
        "Energy infrastructure is becoming a defining constraint of the AI and digital economy.",
        "Hyperscale compute, industrial automation, and electrified supply chains require power systems designed for scale, resilience, and long-term capital durability.",
        "Business-Catalysts advises on the commercial architecture behind large-scale energy platforms including generation, storage, and integrated energy ecosystems supporting digital and industrial infrastructure.",
        "Our focus is not engineering delivery.",
        "It is capital durability, underwriting discipline, and expansion governance across energy infrastructure investments."
      ]
    }
  },

  perspective: {
    title: "Perspective",
    
    intro: {
      title: "Why Perspective Matters",
      paragraphs: [
        "In periods of change, noise travels faster than insight.",
        "These perspectives are shaped by operating in complexity."
      ]
    },
    
    perspectives: [
      {
        title: "Systems Over Tools",
        lines: [
          "Tools enable.",
          "Systems sustain."
        ]
      },
      {
        title: "Why Pilots Rarely Scale",
        lines: [
          "Pilots are protected.",
          "Scale exposes ownership and governance gaps."
        ]
      },
      {
        title: "Governance Is Part of Execution",
        lines: [
          "Governance enables speed to persist.",
          "Trust is designed."
        ]
      },
      {
        title: "The Operator's Mindset",
        lines: [
          "Operators design for real constraints and long-term consequence."
        ]
      },
      {
        title: "AI Does Not Remove Complexity",
        lines: [
          "AI accelerates decision velocity.",
          "It does not automatically create execution coherence.",
          "As systems scale, governance, accountability, ecosystem alignment, and institutional discipline become more important, not less.",
          "Technology amplifies both capability and operational weakness."
        ]
      },
      {
        title: "On Restraint",
        lines: [
          "Clarity compounds.",
          "Noise distracts."
        ]
      }
    ],
    
    closing: {
      title: "Closing Note",
      text: "These perspectives are intentionally few and stable."
    }
  }
};
